import React, { FunctionComponent, useRef, useState } from "react";
import { FormControl, MenuItem, SelectChangeEvent } from "@mui/material";
import { paletteTheme } from "styles/themes/palette";
import { useIntl } from "react-intl";
import { Options } from "constants/options/option";
import FilterTooltip from "components/molecules/tooltip/FilterTooltip";
import { filterTooltipUtils } from "utils/filterTooltipUtils";
import { useFilterTooltip } from "hooks/tooltip/useFilterTooltip";
import { menuPropsStyles, selectStyles } from "styles/constants/checkbox/MultichoiceCheckboxStyles";
import Select from "components/atoms/select/Select";
import styles from "./customSelect.module.scss";
import { InputLabel } from "components/atoms/input/InputLabel";

export interface CustomSelectProps {
  value: Options;
  options: Array<Options>;
  label?: string;
  selectWidth?: number;
  onChange: (value: Options) => void;
  disabled?: boolean;
}

export const CustomSelect: FunctionComponent<CustomSelectProps> = ({
  value,
  options,
  label,
  selectWidth = 108,
  onChange,
  disabled = false,
}) => {
  const intl = useIntl();

  const handleChange = (event: SelectChangeEvent) => {
    const currentIndex = event.target.value ? +event.target.value : 0;
    onChange(options[currentIndex]);
  };
  const [labelHover, setLabelHover] = useState<boolean>(false);

  const {
    displayTooltip,
    setDisplayTooltip,
    closed, // Needed for the case when the user opens the filter with enter key
    handleToggleFilter,
  } = useFilterTooltip();

  const handleMouseEvent = (enter: boolean) => {
    setDisplayTooltip(enter);
    setLabelHover(enter);
  };

  const handleToggleSelect = (closed: boolean) => {
    handleToggleFilter(closed);
    setLabelHover(false);
  };

  const divRef = useRef<HTMLDivElement>();
  const isOverflow = filterTooltipUtils.isOverflow(divRef.current?.firstElementChild);

  return (
    <FilterTooltip
      overflowText={label}
      placement="top"
      openTooltip={isOverflow && displayTooltip && closed}
      isPopoverFilter>
      <FormControl>
        <div ref={divRef}>
          <InputLabel
          values={[]}
          className="custom"
            sx={{
              "&.MuiInputLabel-root:not(.Mui-focused)": {
                color: labelHover ? paletteTheme.colors.DEFAULT_TEXT_COLOR : "",
                "&.MuiInputLabel-root": {
                  maxWidth: "120px",
                },
              },
            }}>
            {label}
          </InputLabel>
        </div>

        <Select
          label={label}
          fullWidth
          value={value}
          classes={{ select: styles["custom-select"] }}
          onMouseEnter={() => handleMouseEvent(true)}
          onMouseLeave={() => handleMouseEvent(false)}
          onOpen={() => handleToggleSelect(false)}
          onClose={() => handleToggleSelect(true)}
          onChange={handleChange}
          renderValue={(_value: Options) => intl.formatMessage({ id: _value.name })}
          MenuProps={menuPropsStyles}
          sx={selectStyles(selectWidth, true)}>
          {options.length > 0 &&
            options.map((option, idx) => (
              <MenuItem
                key={option.id}
                value={idx}
                classes={{ root: styles["item-list"] }}
                sx={{
                  color: value === options[idx] ? "" : "#BABABA",
                }}>
                {intl.formatMessage({ id: option.name })}
              </MenuItem>
            ))}
        </Select>
      </FormControl>
    </FilterTooltip>
  );
};
